import { inject, Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ICtaForm } from '@campaign-form/campaign-form.interface';
import { TranslateService } from '@ngx-translate/core';
import { EPlatformFeature } from '@shared/enums/campaign.enum';
import { camelCase } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { ICtaTemplateApi } from './cta-dialog.interface';
import { ICtaOption } from './cta-select.interface';
import { TDeliverableForm } from './deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { IExecutionForm } from './deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { ExecutionService } from './deliverable-flow-dialog/execution-step/execution-list/execution/execution.service';

@Injectable({
  providedIn: 'root',
})
export class CtaDetailsListService {
  private readonly executionService = inject(ExecutionService);
  private readonly translate = inject(TranslateService);

  public readonly platformFeatureOrder: Readonly<Record<EPlatformFeature, number>> = {
    [EPlatformFeature.twitchStream]: 0,
    [EPlatformFeature.instagramPost]: 1,
    [EPlatformFeature.instagramReel]: 2,
    [EPlatformFeature.instagramStory]: 3,
    [EPlatformFeature.tiktokPost]: 4,
    [EPlatformFeature.youtubeStream]: 5,
    [EPlatformFeature.youtubeVod]: 6,
    [EPlatformFeature.youtubeShort]: 7,
    [EPlatformFeature.facebookPost]: 8,
    [EPlatformFeature.facebookVideo]: 9,
    [EPlatformFeature.xPost]: 10,
  } as const;

  public insertNewCta(
    formArray: FormArray<FormGroup<ICtaForm>>,
    ctaOption: ICtaOption,
    isAllCreators: boolean,
    existingCtaForm?: FormGroup<ICtaForm>,
  ): FormGroup<ICtaForm> {
    const ctaDetailsForm =
      existingCtaForm ??
      new FormGroup<ICtaForm>({
        customCtaValues: new FormControl([], { nonNullable: true }),
        deliverables: new FormArray<FormGroup<TDeliverableForm>>([]),
        exclusiveTo: new FormControl(null),
        executions: new FormArray<FormGroup<IExecutionForm>>([
          this.executionService.getConfig().createForm(ctaOption.platformFeature),
        ]),
        icon: new FormControl(ctaOption.icon),
        id: new FormControl(uuidv4()),
        isAllCreators: new FormControl(isAllCreators),
        isCtaDetailsValid: new FormControl(true),
        isCustomMode: new FormControl(false),
        name: new FormControl(
          ctaOption.name ?? `${this.translate.instant(`shared.platformFeatureGroups.${ctaOption.platformFeature}`)}`,
          [Validators.required, Validators.maxLength(255)],
        ),
        platformFeature: new FormControl(ctaOption.platformFeature, {
          nonNullable: true,
        }),
      });

    const insertionIndex: number = this.findInsertionIndex(formArray, ctaOption.platformFeature);
    formArray.insert(insertionIndex, ctaDetailsForm);

    return ctaDetailsForm;
  }

  public insertExistingCta(formArray: FormArray<FormGroup<ICtaForm>>, ctaForm: FormGroup<ICtaForm>): void {
    const insertionIndex: number = this.findInsertionIndex(formArray, ctaForm.get('platformFeature')!.value);
    formArray.insert(insertionIndex, ctaForm);
  }

  public orderCtaTemplates(ctaTemplates: ICtaTemplateApi[]): ICtaTemplateApi[] {
    return ctaTemplates.sort(
      (a, b) =>
        this.platformFeatureOrder[camelCase(a.platformFeature) as EPlatformFeature] -
        this.platformFeatureOrder[camelCase(b.platformFeature) as EPlatformFeature],
    );
  }

  private findInsertionIndex(formArray: FormArray<FormGroup<ICtaForm>>, platformFeature: EPlatformFeature): number {
    const targetPlatformFeatureOrder = this.platformFeatureOrder[platformFeature];

    let lastSamePlatformIndex = -1;

    for (let i = 0; i < formArray.length; i++) {
      const currentForm = formArray.at(i);
      const currentFormPlatformFeature = currentForm.get('platformFeature')!.value;
      const currentFormPlatformFeatureOrder = this.platformFeatureOrder[currentFormPlatformFeature];

      if (currentFormPlatformFeatureOrder === targetPlatformFeatureOrder) {
        lastSamePlatformIndex = i;

        continue;
      }

      if (currentFormPlatformFeatureOrder > targetPlatformFeatureOrder) {
        return i;
      }
    }

    if (lastSamePlatformIndex !== -1) {
      return lastSamePlatformIndex + 1;
    }

    return formArray.length;
  }
}
